import React from 'react';
import { Control, Controller, UseFormWatch } from 'react-hook-form';
import { Box, Checkbox, Typography } from '@mui/material';
import type { ProductFormValues } from '@/types/product-type';

interface Props {
  control: Control<ProductFormValues | any>;
  watch: UseFormWatch<ProductFormValues | any>;
}

const colorOptions = [
  { label: 'Black / ខ្មៅ', value: 'Black', colorCode: '#000000' },
  { label: 'White / ស', value: 'White', colorCode: '#FFFFFF' },
  { label: 'Gray / ប្រផេះ', value: 'Gray', colorCode: '#808080' },
  { label: 'Red / ក្រហម', value: 'Red', colorCode: '#EF4444' },
  { label: 'Green / បៃតង', value: 'Green', colorCode: '#22c55e' },
  { label: 'Blue / ខៀវ', value: 'Blue', colorCode: '#3b82f6' },
  { label: 'Yellow / លឿង', value: 'Yellow', colorCode: '#eab308' },
  { label: 'Orange / ទឹកក្រូច', value: 'Orange', colorCode: '#f97316' },
  { label: 'Purple / ស្វាយ', value: 'Purple', colorCode: '#a855f7' },
  { label: 'Pink / ផ្កាឈូក', value: 'Pink', colorCode: '#ec4899' },
  { label: 'Brown / ត្នោត', value: 'Brown', colorCode: '#92400e' },
  { label: 'Navy / ខៀវចាស់', value: 'Navy', colorCode: '#1e3a5f' },
  { label: 'Beige / ត្នោតស្រាល', value: 'Beige', colorCode: '#d4b896' },
  { label: 'Khaki / កាគី', value: 'Khaki', colorCode: '#C3B091' },
  { label: 'Maroon / ក្រហមចាស់', value: 'Maroon', colorCode: '#800000' },
];

const sizeOptions = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

export function ProductAttributesWizardSection({ control, watch }: Props) {
  const selectedColors = (watch('colorValues') ?? []) as string[];
  const selectedSizes = (watch('sizeValues') ?? []) as string[];

  const c = Math.max(selectedColors.length, 1);
  const s = Math.max(selectedSizes.length, 1);
  const variantCount = (selectedColors.length > 0 || selectedSizes.length > 0) ? c * s : 0;

  const toggle = (
    currentValues: string[],
    value: string,
    onChange: (v: string[]) => void,
  ) => {
    if (currentValues.includes(value)) {
      onChange(currentValues.filter((v) => v !== value));
    } else {
      onChange([...currentValues, value]);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Size */}
      <Controller
        name="sizeValues"
        control={control}
        defaultValue={[]}
        render={({ field }) => {
          const values = Array.isArray(field.value) ? field.value : [];
          return (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Size <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                {values.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {values.length} selected
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1,
                }}
              >
                {sizeOptions.map((size) => {
                  const checked = values.includes(size);
                  return (
                    <Box
                      key={size}
                      onClick={() => toggle(values, size, field.onChange)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: checked ? '#2563eb' : '#e2e8f0',
                        bgcolor: checked ? '#eff6ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: '#2563eb' },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        disableRipple
                        sx={{
                          p: 0,
                          color: '#cbd5e1',
                          '&.Mui-checked': { color: '#2563eb' },
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggle(values, size, field.onChange)}
                      />
                      <Typography variant="body2" fontWeight={checked ? 600 : 400}>
                        {size}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        }}
      />
      {/* Color */}
      <Controller
        name="colorValues"
        control={control}
        defaultValue={[]}
        render={({ field }) => {
          const values = Array.isArray(field.value) ? field.value : [];
          return (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Color <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                {values.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {values.length} selected
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {colorOptions.map((opt) => {
                  const checked = values.includes(opt.value);
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => toggle(values, opt.value, field.onChange)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: checked ? '#2563eb' : '#e2e8f0',
                        bgcolor: checked ? '#eff6ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: '#2563eb', bgcolor: '#f8faff' },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        disableRipple
                        sx={{
                          p: 0,
                          color: '#cbd5e1',
                          '&.Mui-checked': { color: '#2563eb' },
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggle(values, opt.value, field.onChange)}
                      />
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: opt.colorCode,
                          border: opt.colorCode === '#FFFFFF' ? '1px solid #e2e8f0' : 'none',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">{opt.label}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        }}
      />



      {/* Variants Preview */}
      {(selectedColors.length > 0 || selectedSizes.length > 0) && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: '#eff6ff',
            border: '1px solid #bfdbfe',
          }}
        >
          <Typography variant="subtitle2" color="#2563eb" fontWeight={600} mb={0.5}>
            Variants Preview
          </Typography>
          <Typography variant="body2" color="#1e40af">
            {selectedColors.length} color(s) × {selectedSizes.length} size(s) ={' '}
            <strong>{variantCount} variants</strong> will be created
          </Typography>
        </Box>
      )}
    </Box>
  );
}
